/**
 * Цепочка уровней — единственный жест возврата наверх.
 *
 * Человек, спустившийся из района в массив, оттуда в хозяйство и дальше в
 * контур, обязан видеть весь путь и вернуться на ЛЮБУЮ его ступень одним
 * нажатием. Кнопка «назад» в браузере этого не даёт: она возвращает на
 * предыдущий экран, а не на предыдущий уровень, и по дороге теряет разобранный
 * контекст анализа.
 *
 * Что зафиксировано формой:
 *
 * * **ступень называет свой уровень.** «Массив» и «Хозяйство» подписаны, а не
 *   угадываются по имени: «Кук-Орол» само по себе не говорит, территория это
 *   или организация;
 * * **порядок ступеней — объявленный порядок уровней.** Цепочка собирается по
 *   `levels.ts`, а не по истории переходов: путь читается одинаково, откуда бы
 *   человек ни пришёл;
 * * **последняя ступень — не ссылка.** Ссылка «сам на себя» выглядит как
 *   действие и не является им.
 */

import { Link } from "react-router-dom";
import { levelPath, type LevelId } from "../lib/levels";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import styles from "./trail.module.css";

const m = defineMessages({
  district: { id: "common.kit.trail.district", defaultMessage: "Картина района", description: "Первая ступень цепочки уровней над заголовком экрана: ссылка на обзор района; совпадает с пунктом меню" },
  ariaLabel: { id: "common.kit.trail.ariaLabel", defaultMessage: "Уровень просмотра", description: "aria-label цепочки уровней (район → массив → хозяйство → контур → работа) для экранных дикторов" },
});

/**
 * Подписи уровней на экране. `lib/levels.ts` их читает node-скрипт выгрузки на
 * сервер, поэтому русские слова там остаются как есть, а на экран уровень
 * выходит через эти сообщения по его `id`.
 */
const LEVEL_LABEL = defineMessages({
  district: { id: "common.kit.level.district", defaultMessage: "Район", description: "Название уровня просмотра: район в целом; подпись над именем в цепочке уровней, одно слово" },
  massif: { id: "common.kit.level.massif", defaultMessage: "Массив", description: "Название уровня просмотра: массив (группа хозяйств и земель района); одно слово" },
  actor: { id: "common.kit.level.actor", defaultMessage: "Хозяйство", description: "Название уровня просмотра: хозяйство; одно слово" },
  contour: { id: "common.kit.level.contour", defaultMessage: "Контур", description: "Название уровня просмотра: контур (участок земли по кадастру); одно слово" },
  work: { id: "common.kit.level.work", defaultMessage: "Работа", description: "Название уровня просмотра: работа (агроприём вроде полива или посева); одно слово" },
});

/** Слово уровня на языке интерфейса. */
export function levelLabel(id: LevelId): string {
  return tr(LEVEL_LABEL[id]);
}

export interface TrailStep {
  level: LevelId;
  /**
   * Слово уровня, если ступень называет не одну единицу, а их число
   * («МАССИВЫ 3»). По умолчанию берётся объявленное имя уровня: подменять его
   * ради красоты нельзя, иначе ступень перестаёт называть свой уровень.
   */
  caption?: string;
  /** Имя единицы либо название списка уровня. */
  label: string;
  /**
   * Куда ведёт ступень вместе с контекстом анализа. `null` — ступень не
   * нажимается: это либо текущая позиция, либо величина, у которой одного
   * адреса нет (несколько массивов у одного хозяйства).
   */
  to: { pathname: string; search: string } | null;
}

/**
 * Ступень района — общая для всех экранов, поэтому собирается здесь, а не
 * переписывается на каждом. Имя одно и то же везде: место, называемое на
 * разных экранах по-разному, читается как разные места. По этой же причине оно
 * дословно совпадает с пунктом меню и заголовком экрана `/`.
 */
export function districtStep(linkSearch: string): TrailStep {
  return {
    level: "district",
    label: tr(m.district),
    to: { pathname: levelPath("district"), search: linkSearch },
  };
}

export function LevelTrail({
  steps,
  screen,
}: {
  steps: ReadonlyArray<TrailStep>;
  /**
   * Имя экрана внутри последнего уровня цепочки: «Реестр контуров»,
   * «Проверка», «Карантин».
   *
   * Оно стоит отдельным полем, а не ступенью со своим уровнем, потому что
   * список — не единица учёта: подписать «Реестр контуров» словом «РАЙОН»
   * значило бы объявить экран территорией. Уровень при этом назван — ступенью
   * района слева, а сам экран закрывает цепочку и ссылкой не бывает.
   */
  screen?: string;
}) {
  const t = useT();
  if (steps.length === 0 && !screen) return null;

  // Текущая позиция ровно одна. Если экран назван, ею становится он, и
  // ненажимаемая ступень выше (например «МАССИВЫ 3») текущей не притворяется.
  const currentIndex = screen ? -1 : steps.length - 1;

  return (
    <nav className={styles.trail} aria-label={t(m.ariaLabel)}>
      <ol className={styles.list}>
        {steps.map((step, index) => (
          <li className={styles.step} key={`${step.level}:${step.label}`}>
            <span className={styles.level}>
              {step.caption ?? levelLabel(step.level)}
            </span>
            {step.to ? (
              <Link className={styles.link} to={step.to}>
                {step.label}
              </Link>
            ) : index === currentIndex ? (
              <span className={styles.current} aria-current="page">
                {step.label}
              </span>
            ) : (
              <span className={styles.plain}>{step.label}</span>
            )}
          </li>
        ))}
        {screen ? (
          <li className={styles.step}>
            <span className={styles.current} aria-current="page">
              {screen}
            </span>
          </li>
        ) : null}
      </ol>
    </nav>
  );
}
